import { Component, Inject, OnInit, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AddCustomTheme, GetCustomThemes, UpdateCustomTheme } from '@app/core/store/app.actions';
import { AppState } from '@app/core/store/app.state';
import { CustomTheme, ThemesError } from '@app/_models/themes/theme.model';
import { McToastService } from '@app/_services';
import { Select, Store } from '@ngxs/store';
import { Observable, debounceTime } from 'rxjs';

type CustomThemeData = {
    create: boolean;
    customTheme: CustomTheme;
};
@Component({
    selector: 'app-customize-theme-modal',
    templateUrl: './customize-theme-modal.component.html',
    styleUrls: ['./customize-theme-modal.component.scss'],
})
export class CustomizeThemeModalComponent implements OnInit {
    customThemeContentForm: UntypedFormGroup;
    customThemeHeaderForm: UntypedFormGroup;
    customThemeFooterForm: UntypedFormGroup;
    customThemeButtonsForm: UntypedFormGroup;
    customThemeBodyForm: UntypedFormGroup;
    loading: boolean = false;
    showErrors: boolean = false;
    submitted: boolean = false;
    customThemeId: number;
    @Input() tooltipCustomClass: string;
    @Input() numericCodeNumber: string;
    @Input() trigger: string = 'click';

    @Select(AppState.themesError)
    themesError$: Observable<ThemesError>;

    @Select(AppState.customThemeList)
    customThemeList$: Observable<string>;

    readonly TEXT_COLOR_DEFAULT = '#FFFFFF';
    readonly BG_COLOR_DEFAULT = '#000000';

    constructor(
        public dialogRef: MatDialogRef<CustomizeThemeModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: CustomThemeData,
        private store: Store,
        public fb: UntypedFormBuilder,
        public mcToast: McToastService
    ) {
        this.customThemeId = data?.customTheme?.id ?? null;
    }

    ngOnInit(): void {
        this.store.dispatch(new GetCustomThemes());
        this.buildForms(this.data.customTheme);
    }

    submit(): void {
        this.submitted = true;
        if (this.customThemeContentForm.valid) {
            const { name, ...contentData } = this.customThemeContentForm.value;
            const headerData = this.customThemeHeaderForm.value;
            const footerData = this.customThemeFooterForm.value;
            const buttonsData = this.customThemeButtonsForm.value;
            const bodyData = this.customThemeBodyForm.value;
            if (this.customThemeId) {
                const customTheme: CustomTheme = {
                    id: this.customThemeId,
                    name: name,
                    body: bodyData,
                    content: contentData,
                    header: headerData,
                    footer: footerData,
                    buttons: buttonsData,
                };
                this.updateCustomTheme(customTheme);
            } else {
                const customTheme: CustomTheme = {
                    name: name,
                    body: bodyData,
                    content: contentData,
                    header: headerData,
                    footer: footerData,
                    buttons: buttonsData,
                };
                this.createCustomTheme(customTheme);
            }
        }
    }

    createCustomTheme(customTheme: CustomTheme): void {
        this.loading = true;
        this.store.dispatch(new AddCustomTheme(customTheme)).subscribe({
            next: () => {
                this.loading = false;
                this.showErrors = true;
                this.data.customTheme = customTheme;
                this.store.dispatch(new GetCustomThemes());
            },
            error: () => {
                this.loading = false;
            },
        });
    }

    updateCustomTheme(customTheme: CustomTheme): void {
        const updatedCustomTheme: CustomTheme = {
            id: this.data.customTheme.id,
            ...customTheme,
        };
        this.loading = true;
        this.store.dispatch(new UpdateCustomTheme(updatedCustomTheme)).subscribe({
            next: () => {
                this.loading = false;
                this.showErrors = true;
                this.data.customTheme = updatedCustomTheme;
                this.store.dispatch(new GetCustomThemes());
                this.dialogRef.close(this.data.customTheme);
            },
            error: () => {
                this.loading = false;
            },
        });
    }

    buildForms(customTheme: CustomTheme): void {
        const { body, content, header, footer, buttons } = customTheme;
        this.customThemeBodyForm = this.fb.group({
            background_color: [body.background_color, Validators.required],
        });

        this.customThemeContentForm = this.fb.group({
            name: [customTheme.name, Validators.required],
            font: [content.font, Validators.required],
            primary_color: [content.primary_color, Validators.required],
            secondary_color: [content.secondary_color, Validators.required],
            primary_color_input: [content.primary_color, Validators.required],
            secondary_color_input: [content.secondary_color, Validators.required],
        });

        this.customThemeContentForm.get('primary_color').valueChanges.subscribe((color) => {
            this.customThemeContentForm.get('primary_color_input').setValue(color, { emitEvent: false });
        });

        this.customThemeContentForm
            .get('primary_color_input')
            .valueChanges.pipe(debounceTime(1500))
            .subscribe((color) => {
                const esColorHexadecimal = (str: string): boolean => /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/i.test(str);
                if (esColorHexadecimal(color)) {
                    this.customThemeContentForm.get('primary_color').setValue(color, { emitEvent: false });
                } else {
                    this.customThemeContentForm
                        .get('primary_color')
                        .setValue(this.TEXT_COLOR_DEFAULT, { emitEvent: false });
                    this.customThemeContentForm
                        .get('text_color_input')
                        .setValue(this.TEXT_COLOR_DEFAULT, { emitEvent: false });
                }
            });

        this.customThemeContentForm.get('secondary_color').valueChanges.subscribe((color) => {
            this.customThemeContentForm.get('secondary_color_input').setValue(color, { emitEvent: false });
        });

        this.customThemeContentForm
            .get('secondary_color_input')
            .valueChanges.pipe(debounceTime(1500))
            .subscribe((color) => {
                const esColorHexadecimal = (str: string): boolean => /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/i.test(str);
                if (esColorHexadecimal(color)) {
                    this.customThemeContentForm.get('secondary_color').setValue(color, { emitEvent: false });
                } else {
                    this.customThemeContentForm
                        .get('secondary_color')
                        .setValue(this.BG_COLOR_DEFAULT, { emitEvent: false });
                    this.customThemeContentForm
                        .get('secondary_color_input')
                        .setValue(this.BG_COLOR_DEFAULT, { emitEvent: false });
                }
            });

        this.customThemeHeaderForm = this.fb.group({
            color: [header.color, Validators.required],
            font_color: [header.font_color, Validators.required],
            color_input: [header.color, Validators.required],
            font_color_input: [header.font_color, Validators.required],
        });

        this.customThemeHeaderForm.get('color').valueChanges.subscribe((color) => {
            this.customThemeHeaderForm.get('color_input').setValue(color, { emitEvent: false });
        });

        this.customThemeHeaderForm
            .get('color_input')
            .valueChanges.pipe(debounceTime(1500))
            .subscribe((color) => {
                const esColorHexadecimal = (str: string): boolean => /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/i.test(str);
                if (esColorHexadecimal(color)) {
                    this.customThemeHeaderForm.get('color').setValue(color, { emitEvent: false });
                } else {
                    this.customThemeHeaderForm.get('color').setValue(this.TEXT_COLOR_DEFAULT, { emitEvent: false });
                    this.customThemeHeaderForm
                        .get('color_input')
                        .setValue(this.TEXT_COLOR_DEFAULT, { emitEvent: false });
                }
            });

        this.customThemeHeaderForm.get('font_color').valueChanges.subscribe((color) => {
            this.customThemeHeaderForm.get('font_color_input').setValue(color, { emitEvent: false });
        });

        this.customThemeHeaderForm
            .get('font_color_input')
            .valueChanges.pipe(debounceTime(1500))
            .subscribe((color) => {
                const esColorHexadecimal = (str: string): boolean => /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/i.test(str);
                if (esColorHexadecimal(color)) {
                    this.customThemeHeaderForm.get('font_color').setValue(color, { emitEvent: false });
                } else {
                    this.customThemeHeaderForm.get('font_color').setValue(this.BG_COLOR_DEFAULT, { emitEvent: false });
                    this.customThemeHeaderForm
                        .get('font_color_input')
                        .setValue(this.BG_COLOR_DEFAULT, { emitEvent: false });
                }
            });

        this.customThemeFooterForm = this.fb.group({
            color: [footer.color, Validators.required],
            font_color: [footer.font_color, Validators.required],
            color_input: [footer.color, Validators.required],
            font_color_input: [footer.font_color, Validators.required],
        });

        this.customThemeFooterForm.get('color').valueChanges.subscribe((color) => {
            this.customThemeFooterForm.get('color_input').setValue(color, { emitEvent: false });
        });

        this.customThemeFooterForm
            .get('color_input')
            .valueChanges.pipe(debounceTime(1500))
            .subscribe((color) => {
                const esColorHexadecimal = (str: string): boolean => /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/i.test(str);
                if (esColorHexadecimal(color)) {
                    this.customThemeFooterForm.get('color').setValue(color, { emitEvent: false });
                } else {
                    this.customThemeFooterForm.get('color').setValue(this.TEXT_COLOR_DEFAULT, { emitEvent: false });
                    this.customThemeFooterForm
                        .get('color_input')
                        .setValue(this.TEXT_COLOR_DEFAULT, { emitEvent: false });
                }
            });

        this.customThemeFooterForm.get('font_color').valueChanges.subscribe((color) => {
            this.customThemeFooterForm.get('font_color_input').setValue(color, { emitEvent: false });
        });

        this.customThemeFooterForm
            .get('font_color_input')
            .valueChanges.pipe(debounceTime(1500))
            .subscribe((color) => {
                const esColorHexadecimal = (str: string): boolean => /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/i.test(str);
                if (esColorHexadecimal(color)) {
                    this.customThemeFooterForm.get('font_color').setValue(color, { emitEvent: false });
                } else {
                    this.customThemeFooterForm.get('font_color').setValue(this.BG_COLOR_DEFAULT, { emitEvent: false });
                    this.customThemeFooterForm
                        .get('font_color_input')
                        .setValue(this.BG_COLOR_DEFAULT, { emitEvent: false });
                }
            });

        this.customThemeButtonsForm = this.fb.group({
            border_radius: [buttons.border_radius, Validators.required],
        });
    }

    close(): void {
        this.dialogRef.close(this.data.customTheme);
    }
}
